import { useEffect, useRef } from "react";
import { Search } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const featureItems = ["Supplier-specific emissions", "Finance emissions", "Audit-ready reports", "Audit workflow", "Emission Factor referencing", "Significance", "Uncertainty", "Recalculation", "Traceability", "AR5 / AR6 options"];

const IndustryExpertiseBento = () => {
  const { t } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const duplicatedItems = [...featureItems, ...featureItems, ...featureItems];

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateScanEffect = () => {
      const items = container.querySelectorAll('.scan-item');
      const containerRect = container.getBoundingClientRect();
      const centerY = containerRect.top + containerRect.height / 2;

      // Find the item closest to center
      let closestItem: Element | null = null;
      let closestDistance = Infinity;

      items.forEach((item) => {
        const itemRect = item.getBoundingClientRect();
        const itemCenterY = itemRect.top + itemRect.height / 2;
        const distance = Math.abs(centerY - itemCenterY);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestItem = item;
        }
      });

      // Apply styles: only the closest item gets the magnified effect
      items.forEach((item) => {
        const isActive = item === closestItem && closestDistance < 40;
        
        if (isActive) {
          (item as HTMLElement).style.transform = 'scale(1.4, 1.35)';
          (item as HTMLElement).style.opacity = '1';
          (item as HTMLElement).style.filter = 'brightness(1.2)';
          (item as HTMLElement).style.boxShadow = '0 0 25px rgba(52, 211, 153, 0.6)';
          (item as HTMLElement).style.borderColor = 'rgba(110, 231, 183, 0.9)';
          (item as HTMLElement).style.background = 'rgba(255, 255, 255, 0.45)';
        } else {
          (item as HTMLElement).style.transform = 'scale(1)';
          (item as HTMLElement).style.opacity = '0.6';
          (item as HTMLElement).style.filter = 'brightness(0.9)';
          (item as HTMLElement).style.boxShadow = 'none';
          (item as HTMLElement).style.borderColor = 'rgba(255,255,255,0.3)';
          (item as HTMLElement).style.background = 'rgba(255, 255, 255, 0.2)';
        }
      });

      requestAnimationFrame(updateScanEffect);
    };

    const animationId = requestAnimationFrame(updateScanEffect);
    return () => cancelAnimationFrame(animationId);
  }, []);

  return (
    <div className="w-full h-[300px] lg:h-[400px]">
      <div className="h-full bg-gradient-to-br from-primary/90 to-primary rounded-3xl p-8 text-primary-foreground relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />
        
        {/* Decorative magnifying glass icons */}
        <Search className="absolute top-6 left-12 w-5 h-5 text-white/20 rotate-12" />
        <Search className="absolute top-16 left-32 w-4 h-4 text-white/15 -rotate-6" />
        <Search className="absolute bottom-8 left-16 w-6 h-6 text-white/20 rotate-45" />
        <Search className="absolute bottom-20 left-40 w-3 h-3 text-white/15 -rotate-12" />
        <Search className="absolute top-12 left-56 w-4 h-4 text-white/15 rotate-12" />
        <Search className="absolute bottom-12 left-8 w-4 h-4 text-white/15 -rotate-24" />
        <Search className="absolute top-8 right-24 w-5 h-5 text-white/20 -rotate-12" />
        <Search className="absolute top-20 right-48 w-4 h-4 text-white/15 rotate-24" />
        <Search className="absolute bottom-10 right-32 w-6 h-6 text-white/20 rotate-12" />
        <Search className="absolute bottom-24 right-16 w-3 h-3 text-white/15 -rotate-45" />
        
        <div className="relative z-10 h-full flex items-center">
          <div className="flex flex-col flex-1 pr-4 justify-center items-start mt-8">
            <h3 className="text-xl lg:text-2xl font-bold mb-2">
              {t("features.auditDesigned")}<br />{t("features.auditTrusted")}
            </h3>
            <p className="text-primary-foreground/80 text-lg max-w-md">
              {t("features.auditPortal")}
            </p>
          </div>

          <div ref={containerRef} className="hidden md:flex items-center justify-center w-1/2 relative overflow-hidden">
            {/* Corner brackets scanning zone */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-16 z-10 pointer-events-none">
              {/* Top-left corner */}
              <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-emerald-400 rounded-tl-sm" />
              {/* Top-right corner */}
              <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-emerald-400 rounded-tr-sm" />
              {/* Bottom-left corner */}
              <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-emerald-400 rounded-bl-sm" />
              {/* Bottom-right corner */}
              <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-emerald-400 rounded-br-sm" />
            </div>
            <div className="flex flex-col items-center gap-3 animate-scroll-up-scan">
              {duplicatedItems.map((item, index) => (
                <div 
                  key={index} 
                  className="px-4 py-2 bg-white/30 backdrop-blur-sm border border-white/40 rounded-full text-sm text-white whitespace-nowrap scan-item transition-all duration-150"
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustryExpertiseBento;